import classNames from 'classnames'
import { useRouter } from 'next-nprogress-bar'
import { useEffect, useState, useCallback, useRef, RefObject, useMemo } from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useProductWishList } from '@src/hooks/useProductWishlist'
import { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import { useEDDDetailFromSelectedOptions } from '@src/quince-site-common/src/hooks/edd/utils/edd.utils'
import { useBatchedAnalyticsCaptureForNewProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsCaptureForProductCard'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIsVisible from '@src/quince-site-common/src/hooks/useIsVisible'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import { useProductDynamicData } from '@src/quince-site-common/src/redux/globalStateManager/productDynamicDataStateManager'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

import { ProductCardLink } from './product-card-actions/product-card-link'
import { ProductCardHoverProvider } from './product-card-context/product-card-hover.context'
import { ProductCardFooter, ProductCardFooterProps } from './product-card-footer'
import {
  getAllVariantIds,
  getInitialSelectedVariant,
  getVariantTags,
  navigateToPDP,
  getProductLink,
} from './product-card.helpers'
import { useRefreshedTags } from './product-card.hooks'
import styles from './product-card.module.scss'
import { CardVariant, IProductCard } from './product-card.types'
import { ProductImageSection, ProductImageSectionProps } from './product-image-section'
import { ProductTitleSection, ProductTitleSectionProps } from './product-title-section'

type FooterProps = Pick<
  ProductCardFooterProps,
  'hideOptions' | 'hideReviews' | 'ratingTextClassName' | 'excessOptionsTextClassName'
>
type TitleProps = Pick<
  ProductTitleSectionProps,
  | 'additionalAttributes'
  | 'hideProductName'
  | 'selectorKey'
  | 'tracker'
  | 'priceClassName'
  | 'productPrefixPriceClassName'
  | 'titleClassName'
  | 'productPriceClass'
  | 'productPriceWrapperClass'
  | 'productCardLinkClass'
  | 'forceUnclickableHref'
>

type ImageProps = Pick<
  ProductImageSectionProps,
  | 'eagerLoadImage'
  | 'imageRef'
  | 'gender'
  | 'maxWidth'
  | 'wishlistIconClassName'
  | 'addToCartClassName'
  | 'deleteItemClassName'
  | 'showDeleteBtn'
  | 'hideTags'
  | 'hideBuyNowIcon'
  | 'quality'
  | 'wishlistTrackConfig'
  | 'wishlistIconClass'
>

export interface ProductCardProps extends FooterProps, TitleProps, ImageProps {
  productCard: IProductCard
  className?: string
  searchQuery?: string
  position?: number
  shouldShowMore?: boolean
  optionLimit?: number
  hideWishlistIcon?: boolean
  hideBuyNowIcon?: boolean
  hideTags?: boolean
  hideTitle?: boolean
  nameAndPriceSectionClass?: string
  productCardImageSectionClass?: string
  productViewTracker?: string
  productClickTrackConfig?: TrackConfig
  showSecondImageOnProductTiles?: boolean
  skipTagRefresh?: boolean
  onShowMoreClick?: (params: { selectedVariant?: CardVariant }) => void
  showMScroll?: boolean
  showWhiteGloveDeliveryTag?: boolean
  customFooter?: JSX.Element
  onClickCb?: () => void
  quickBuyOverride?: () => void
  forceShowBuyNow?: boolean
  allowCustomFooterHover?: boolean
  tabData?: Record<string, string | boolean | { i: number; j: number }>
  selectedCardVariantId?: number
  isPageEligibleForHolidayTag?: boolean
  onVariantChange?: (variant: CardVariant) => void
  hideDeleteForOtherColors?: boolean
  isPostorder?: boolean
}

const DEFAULT_OPTIONS_LIMIT = 5
const DEFAULT_OPTIONS_TO_SHOW = true

export function ProductCardLegacy({
  onShowMoreClick,
  className,
  onClickCb,
  forceShowBuyNow,
  selectedCardVariantId,
  onVariantChange,
  hideDeleteForOtherColors,
  ...props
}: ProductCardProps) {
  const {
    productCard: {
      product: { productId, slug, cardVariants, relatedIds, title, thirdPartyDetails, reviews, flags },
    },
    additionalAttributes,
    showWhiteGloveDeliveryTag,
  } = props
  const router = useRouter()
  const wrapperRef: RefObject<HTMLDivElement> = useRef(null)
  const { isVisible } = useIsVisible(wrapperRef, 0.85) // This is done to support second card visibility on PDP page without scrolling. To be removed once we have a better solution
  const { isNativeView } = useDeviceInfo()
  const isWebView = getIsNativeViewClient()
  const { isItemInWishList } = useProductWishList()

  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  const [selectedVariant, setSelectedVariant] = useState<CardVariant | undefined>(
    getInitialSelectedVariant({ cardVariants: cardVariants, selectedCardVariantId }),
  )
  const [productSearchObjectId, setProductSearchObjectId] = useState('')

  const selectedColor = selectedVariant?.displayConfig.value
  const isCurrentVariantWishlisted = isItemInWishList(relatedIds.spid, selectedColor ?? '')
  const computedShowDeleteBtn = hideDeleteForOtherColors
    ? props.showDeleteBtn && isCurrentVariantWishlisted
    : props.showDeleteBtn

  const handleSetSelectedVariant = useCallback(
    (variant: CardVariant | undefined) => {
      setSelectedVariant(variant)
      if (variant) onVariantChange?.(variant)
    },
    [onVariantChange],
  )

  const handleShowMoreClick = useCallback(
    ({ selectedVariant }: { selectedVariant?: CardVariant }) => {
      if (isWebView) {
        const productLink = getProductLink(slug, selectedVariant?.displayConfig.value, '', additionalAttributes)
        try {
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({
              action: 'NAVIGATION',
              url: window.location.href,
              newURL: productLink.split('?')[0],
              nextURL: productLink,
            }),
          )
          return
        } catch (error) {
          // open in webview if postmessage fails
          router.push(slug)
          console.error('Error in sending message to native webview ', error)
        }
      }
      onClickCb?.()
      if (onShowMoreClick) {
        onShowMoreClick({ selectedVariant })
        return
      }

      navigateToPDP({
        slug: slug,
        selectedOption: selectedVariant?.displayConfig.value,
        additionalAttributes: additionalAttributes,
        tracker: '',
        router,
      })
    },
    [onShowMoreClick, additionalAttributes, slug, router, onClickCb, isWebView],
  )

  useEffect(() => {
    if (!productSearchObjectId && relatedIds.spid && selectedVariant) {
      setProductSearchObjectId(`${relatedIds.spid}_${selectedVariant.displayConfig.value}`)
    }
  }, [relatedIds.spid, productSearchObjectId, selectedVariant])

  useEffect(() => {
    setSelectedVariant(getInitialSelectedVariant({ cardVariants: cardVariants, selectedCardVariantId }))
  }, [cardVariants, selectedCardVariantId])

  const eddRequestedProducts = {
    product_id: productId.toString(),
    variant_ids: getAllVariantIds(cardVariants),
  }

  const {
    productTags: refreshedProductTags,
    isWhiteGloveProduct,
    productEDDDetails,
    productPromoConfig,
    subscriptionEnabled: refreshedSubscriptionEnabled,
  } = useProductDynamicData({
    productId: productId,
    selectedVariantId: selectedVariant?.variantId,
    isProductConstructedOnServer: true,
    skipTagRefresh: props.skipTagRefresh,
    wrapperRef: wrapperRef,
    eddRequestedProducts,
    subscriptionExpEnabled: isRecurringItemSubscriptionsExpEnabled && !props.isPostorder,
  })

  const refreshedTagsForSelectedVariant = useRefreshedTags(
    props.productCard.product.cardVariants,
    selectedVariant,
    refreshedProductTags,
  )

  const { zipCode } = useZipCode()
  const eddDetails = useEDDDetailFromSelectedOptions(
    productEDDDetails,
    selectedVariant?.variantIds,
    zipCode ?? undefined,
  )

  const { applicableTags } = useMemo(() => {
    return getVariantTags(selectedVariant, refreshedTagsForSelectedVariant)
  }, [selectedVariant, refreshedTagsForSelectedVariant])

  const isLoggedIn = isBrowser() ? AuthUtil.isLoggedIn() : false

  useBatchedAnalyticsCaptureForNewProductCard({
    applicableTags,
    productCard: props.productCard,
    selectedVariant: selectedVariant,
    productViewTracker: props.productViewTracker,
    wrapperRef,
    isVisible,
    eddDetails,
  })

  const isProductWhiteGloveDelivery =
    isWhiteGloveProduct ??
    (props.productCard.product.type === PRODUCT_TYPES.NORMAL &&
      !!props.productCard.product.flags.isWhiteGloveDeliveryAvailable)

  const renderFooter = () => {
    if (props.customFooter) {
      return (
        <ProductCardLink
          slug={slug}
          additionalAttributes={additionalAttributes}
          selectorKey={props.selectorKey}
          tracker={props.tracker}
          product={props.productCard.product}
          productClickTrackConfig={props.productClickTrackConfig}
          productCardLinkClass={props.productCardLinkClass}
          selectedCardVariant={selectedVariant}
          siteSearchQuery={props.searchQuery}
          productPosition={props.position}
          productSearchObjectId={productSearchObjectId}
          title={title}
          spid={relatedIds.spid}
          reviewSummary={reviews}
          colorVariants={props.productCard.product.colorVariants}
          eddDetails={eddDetails}
          onClickCb={onClickCb}
          allowCustomFooterHover={props.allowCustomFooterHover}
        >
          {props.customFooter}
        </ProductCardLink>
      )
    }
    return (
      <ProductCardFooter
        productCard={props.productCard}
        selectedVariant={selectedVariant}
        setSelectedVariant={handleSetSelectedVariant}
        cardVariants={cardVariants}
        hideOptions={props.hideOptions}
        hideReviews={props.hideReviews}
        shouldShowMore={props.shouldShowMore ?? DEFAULT_OPTIONS_TO_SHOW}
        optionLimit={props.optionLimit ?? DEFAULT_OPTIONS_LIMIT}
        onShowMoreClick={handleShowMoreClick}
        reviewSummary={reviews}
        productClickTrackConfig={props.productClickTrackConfig}
        isWhiteGloveDelivery={showWhiteGloveDeliveryTag && isProductWhiteGloveDelivery}
        eddDetails={eddDetails}
        isPageEligibleForHolidayTag={props.isPageEligibleForHolidayTag}
        isPostorder={props.isPostorder}
      />
    )
  }

  return (
    <ProductCardHoverProvider>
      <article
        className={classNames(styles.productCard, className)}
        data-testid={TEST_LOCATORS.PRODUCT_CARD}
        data-slug={props.productCard.product.slug}
        ref={wrapperRef}
      >
        {!props.hideTitle ? (
          <ProductTitleSection
            title={title}
            brandName={thirdPartyDetails.displayName ?? thirdPartyDetails.brand}
            hideProductName={props.hideProductName}
            slug={slug}
            selectedCardVariant={selectedVariant}
            selectorKey={props.selectorKey}
            tracker={props.tracker}
            additionalAttributes={additionalAttributes}
            siteSearchQuery={props.searchQuery}
            productPosition={props.position}
            productSearchObjectId={productSearchObjectId}
            priceClassName={props.priceClassName}
            productPrefixPriceClassName={props.productPrefixPriceClassName}
            titleClassName={props.titleClassName}
            productPriceClass={props.productPriceClass}
            productPriceWrapperClass={props.productPriceWrapperClass}
            productCardLinkClass={props.productCardLinkClass}
            nameAndPriceSectionClass={props.nameAndPriceSectionClass}
            productClickTrackConfig={props.productClickTrackConfig}
            spid={relatedIds.spid}
            reviewSummary={reviews}
            colorVariants={props.productCard.product.colorVariants}
            eddDetails={eddDetails}
            product={props.productCard.product}
            onClickCb={onClickCb}
            forceUnclickableHref={props.forceUnclickableHref}
            productPromoConfig={productPromoConfig}
            subscriptionEnabled={Boolean(isRecurringItemSubscriptionsExpEnabled && refreshedSubscriptionEnabled)}
          />
        ) : null}
        <ProductImageSection
          eagerLoadImage={props.eagerLoadImage}
          imageRef={props.imageRef}
          gender={props.gender}
          maxWidth={props.maxWidth}
          wishlistIconClassName={props.wishlistIconClassName}
          wishlistIconClass={props.wishlistIconClass}
          addToCartClassName={classNames(styles.buyItNowBtn, props.addToCartClassName)}
          deleteItemClassName={classNames(styles.deleteBtn, props.deleteItemClassName)}
          applicableTags={applicableTags}
          showDeleteBtn={computedShowDeleteBtn}
          className={classNames(styles.productCardImageSection, props.productCardImageSectionClass)}
          hideWishlistIcon={(isNativeView && !isLoggedIn) || props.hideWishlistIcon}
          hideBuyNowIcon={isNativeView || props.hideBuyNowIcon}
          quality={props.quality}
          hideTags={props.hideTags}
          productCard={props.productCard}
          selectedCardVariant={selectedVariant}
          quickBuyEnabled={flags.quickBuyEnabled}
          wishlistEnabled={flags.wishlistEnabled}
          readyToShipEnabled={flags.readyToShipEnabled}
          siteSearchQuery={props.searchQuery}
          productPosition={props.position}
          productSearchObjectId={productSearchObjectId}
          showSecondImageOnProductTiles={props.showSecondImageOnProductTiles}
          productClickTrackConfig={props.productClickTrackConfig}
          wishlistTrackConfig={props.wishlistTrackConfig}
          showMScroll={false}
          forceShowBuyNow={forceShowBuyNow}
          slug={slug}
          additionalAttributes={additionalAttributes}
          tracker={props.tracker}
          selectorKey={props.selectorKey}
          spid={relatedIds.spid}
          colorVariants={props.productCard.product.colorVariants}
          title={title}
          reviewSummary={reviews}
          eddDetails={eddDetails}
          product={props.productCard.product}
          onClickCb={onClickCb}
          quickBuyOverride={props.quickBuyOverride}
          tabData={props.tabData}
        />
        {renderFooter()}
      </article>
    </ProductCardHoverProvider>
  )
}
